import React from 'react';
import clsx from 'clsx';
import { ThumbsUp } from 'lucide-react';
import {
  setDiscussionReplyLiked,
  setDiscussionTopicLiked,
  setSessionDiscussionReplyLiked,
  subscribeDiscussionReplyLiked,
  subscribeDiscussionTopicLiked,
  subscribeDiscussionTopicLikers,
  subscribeSessionDiscussionReplyLiked
} from '../lib/firestore';
import { formatPersonName } from '../lib/names';
import type { UserProfile } from '../lib/types';

function ReplyLikeButton(props: {
  count: number;
  liked: boolean;
  busy: boolean;
  subject: string;
  saveError?: boolean;
  disabled?: boolean;
  likerNames?: string[];
  onToggle: () => void;
}) {
  const label = props.saveError ? 'Like could not be saved. Try again' : props.liked ? 'Remove like' : `Like this ${props.subject}`;

  return (
    <button
      type="button"
      title={props.likerNames === undefined
        ? label
        : `${label}\n\n${props.likerNames.length ? `Liked by:\n${props.likerNames.join('\n')}` : 'No likes yet'}`}
      aria-label={`${label}. ${props.count} ${props.count === 1 ? 'like' : 'likes'}`}
      aria-pressed={props.liked}
      disabled={props.disabled || props.busy}
      onClick={props.onToggle}
      className={clsx(
        'inline-flex h-7 min-w-10 shrink-0 items-center justify-center gap-1 rounded-full border px-2 text-xs font-medium transition',
        props.saveError
          ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
          : props.liked
          ? 'border-accent/40 bg-accent/10 text-accent hover:bg-accent/15'
          : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900',
        (props.disabled || props.busy) && 'cursor-not-allowed opacity-60'
      )}
    >
      <ThumbsUp className="h-3.5 w-3.5" aria-hidden />
      <span>{props.count}</span>
    </button>
  );
}

export function DiscussionReplyLikeControl(props: {
  topicId: string;
  replyId: string;
  profileId?: string;
  likeCount?: number;
}) {
  const [serverLiked, setServerLiked] = React.useState(false);
  const [optimisticLiked, setOptimisticLiked] = React.useState<boolean | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [saveError, setSaveError] = React.useState(false);
  const pendingRef = React.useRef(false);

  React.useEffect(() => {
    setServerLiked(false);
    setOptimisticLiked(null);
    setSaveError(false);
    if (!props.profileId) return undefined;
    return subscribeDiscussionReplyLiked(props.topicId, props.replyId, props.profileId, (nextLiked) => {
      setServerLiked(nextLiked);
      if (!pendingRef.current) setOptimisticLiked(null);
    });
  }, [props.topicId, props.replyId, props.profileId]);

  async function toggle() {
    if (!props.profileId || busy) return;
    const nextLiked = !(optimisticLiked ?? serverLiked);
    pendingRef.current = true;
    setBusy(true);
    setSaveError(false);
    setOptimisticLiked(nextLiked);
    try {
      await setDiscussionReplyLiked({
        topicId: props.topicId,
        replyId: props.replyId,
        profileId: props.profileId,
        liked: nextLiked
      });
      setServerLiked(nextLiked);
      setOptimisticLiked(null);
    } catch {
      setOptimisticLiked(null);
      setSaveError(true);
    } finally {
      pendingRef.current = false;
      setBusy(false);
    }
  }

  const liked = optimisticLiked ?? serverLiked;
  const baseCount = Math.max(0, Number(props.likeCount || 0));
  const optimisticDelta = optimisticLiked == null || optimisticLiked === serverLiked ? 0 : optimisticLiked ? 1 : -1;
  const count = Math.max(liked ? 1 : 0, baseCount + optimisticDelta);

  return (
    <ReplyLikeButton
      count={count}
      liked={liked}
      busy={busy}
      subject="reply"
      saveError={saveError}
      disabled={!props.profileId}
      onToggle={() => void toggle()}
    />
  );
}

export function DiscussionTopicLikeControl(props: {
  topicId: string;
  profileId?: string;
  likeCount?: number;
  profilesById: Map<string, UserProfile>;
}) {
  const [likerIds, setLikerIds] = React.useState<string[] | undefined>();
  const [serverLiked, setServerLiked] = React.useState(false);
  const [optimisticLiked, setOptimisticLiked] = React.useState<boolean | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [saveError, setSaveError] = React.useState(false);
  const pendingRef = React.useRef(false);

  React.useEffect(() => {
    setLikerIds(undefined);
    return subscribeDiscussionTopicLikers(props.topicId, setLikerIds, () => setLikerIds(undefined));
  }, [props.topicId]);

  React.useEffect(() => {
    setServerLiked(false);
    setOptimisticLiked(null);
    setSaveError(false);
    if (!props.profileId) return undefined;
    return subscribeDiscussionTopicLiked(props.topicId, props.profileId, (nextLiked) => {
      setServerLiked(nextLiked);
      if (!pendingRef.current) setOptimisticLiked(null);
    });
  }, [props.topicId, props.profileId]);

  async function toggle() {
    if (!props.profileId || busy) return;
    const nextLiked = !(optimisticLiked ?? serverLiked);
    pendingRef.current = true;
    setBusy(true);
    setSaveError(false);
    setOptimisticLiked(nextLiked);
    try {
      await setDiscussionTopicLiked({
        topicId: props.topicId,
        profileId: props.profileId,
        liked: nextLiked
      });
      setServerLiked(nextLiked);
      setOptimisticLiked(null);
    } catch {
      setOptimisticLiked(null);
      setSaveError(true);
    } finally {
      pendingRef.current = false;
      setBusy(false);
    }
  }

  const liked = optimisticLiked ?? serverLiked;
  const baseCount = Math.max(0, Number(props.likeCount || 0));
  const optimisticDelta = optimisticLiked == null || optimisticLiked === serverLiked ? 0 : optimisticLiked ? 1 : -1;
  const count = Math.max(liked ? 1 : 0, baseCount + optimisticDelta);

  return (
    <ReplyLikeButton
      count={count}
      liked={liked}
      busy={busy}
      subject="post"
      likerNames={likerIds?.map((id) => formatPersonName(props.profilesById.get(id)?.displayName) || 'Unknown member')
        .sort((a, b) => a.localeCompare(b))}
      saveError={saveError}
      disabled={!props.profileId}
      onToggle={() => void toggle()}
    />
  );
}

export function SessionDiscussionReplyLikeControl(props: {
  conferenceId: string;
  sessionId: string;
  replyId: string;
  profileId?: string;
  likeCount?: number;
}) {
  const [serverLiked, setServerLiked] = React.useState(false);
  const [optimisticLiked, setOptimisticLiked] = React.useState<boolean | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [saveError, setSaveError] = React.useState(false);
  const pendingRef = React.useRef(false);

  React.useEffect(() => {
    setServerLiked(false);
    setOptimisticLiked(null);
    setSaveError(false);
    if (!props.profileId) return undefined;
    return subscribeSessionDiscussionReplyLiked(
      props.conferenceId,
      props.sessionId,
      props.replyId,
      props.profileId,
      (nextLiked) => {
        setServerLiked(nextLiked);
        if (!pendingRef.current) setOptimisticLiked(null);
      }
    );
  }, [props.conferenceId, props.sessionId, props.replyId, props.profileId]);

  async function toggle() {
    if (!props.profileId || busy) return;
    const nextLiked = !(optimisticLiked ?? serverLiked);
    pendingRef.current = true;
    setBusy(true);
    setSaveError(false);
    setOptimisticLiked(nextLiked);
    try {
      await setSessionDiscussionReplyLiked({
        conferenceId: props.conferenceId,
        sessionId: props.sessionId,
        replyId: props.replyId,
        profileId: props.profileId,
        liked: nextLiked
      });
      setServerLiked(nextLiked);
      setOptimisticLiked(null);
    } catch {
      setOptimisticLiked(null);
      setSaveError(true);
    } finally {
      pendingRef.current = false;
      setBusy(false);
    }
  }

  const liked = optimisticLiked ?? serverLiked;
  const baseCount = Math.max(0, Number(props.likeCount || 0));
  const optimisticDelta = optimisticLiked == null || optimisticLiked === serverLiked ? 0 : optimisticLiked ? 1 : -1;
  const count = Math.max(liked ? 1 : 0, baseCount + optimisticDelta);

  return (
    <ReplyLikeButton
      count={count}
      liked={liked}
      busy={busy}
      subject="reply"
      saveError={saveError}
      disabled={!props.profileId}
      onToggle={() => void toggle()}
    />
  );
}
